import { useTranslation } from 'react-i18next'
import { Button } from '@percent/lemonade'
import type { NativeApplyModalButton } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import { resolveNativeApplyPartnerKey } from '@percent/cause-dashboard/constants/nativeApply/resolveNativeApplyPartner'
import { getResolvedNativeApplyContext } from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'
import { useMarketplaceAnalytics } from '@percent/cause-dashboard/common/hooks'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import styles from './RichTextNativeApplyModalButton.module.scss'

export type RichTextNativeApplyModalButtonProps = {
  blok: NativeApplyModalButton
}

/**
 * Storyblok `native_apply_modal_button` embedded in product-offer detail richtext. Opens the
 * Native Apply modal seeded from the linked `offer` story. Renders nothing when the partner key
 * cannot be resolved or the offer relation is missing/incomplete — we never show a button that
 * would open a modal with empty copy.
 */
export function RichTextNativeApplyModalButton({ blok }: Readonly<RichTextNativeApplyModalButtonProps>) {
  const { t } = useTranslation()
  const { openNativeApply } = useNativeApply()
  const { trackProgramVisited } = useMarketplaceAnalytics()

  const partnerKey = resolveNativeApplyPartnerKey(blok.partner)
  const modalContext = getResolvedNativeApplyContext(blok.offer)
  const label = blok.button_label?.trim() || t('typography.nativeApply.defaultButton')

  if (!partnerKey || !modalContext || !label) {
    return null
  }

  const onClick = () => {
    trackProgramVisited({
      source: MarketplaceProgrammeVisitedSource.ProductPage,
      targetUrl: '',
      isNativeApply: true,
      programmeOfferName: modalContext.title,
      originCategory: OriginCategory.ProductOfferDetail
    })

    openNativeApply({
      partnerKey,
      modalContext,
      tracking: {
        programmeOfferName: modalContext.title,
        source: 'product-offer-detail-richtext',
        originCategory: OriginCategory.ProductOfferDetail
      }
    })
  }

  return (
    <div className={styles.nativeApplyModalButtonBlock}>
      <Button variant="primary" stretch onClick={onClick}>
        {label}
      </Button>
    </div>
  )
}
